// Create a new file: src/services/reactionService.ts
import API_BASE_URL from '../config';
import { reactionTable } from '../db/entityCache';
import type { Reaction } from '../db/types';
import * as hazardGateway from '../db/hazardGateway';
import eventEmitter, { EVENTS } from './eventService';
import moment from 'moment';
import logger, { LogCategory } from '../utils/logger';
import { fetchWithAuth } from '../utils/fetchWithAuth';

const REACTION_TYPE_IDS: Record<string, number> = {
  'low:card': 15,
  'medium:card': 16,
  'high:card': 17,
  'extreme:card': 18,
  'acknowledged:comment': 19,
  'agree:comment': 20,
  'concern:comment': 21,
  'escalate:comment': 22,
  'dismiss:comment': 23,
};

function reactionTypeIdFor(name: string, content: string): number | undefined {
  return REACTION_TYPE_IDS[`${name}:${content}`];
}

interface CardReactionView {
  reaction_counts: Record<string, number>;
  reactions: Reaction[];
}

function readCountMap(value: unknown): Record<string, number> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  const counts: Record<string, number> = {};
  for (const [key, count] of Object.entries(value as Record<string, unknown>)) {
    if (typeof count === 'number' && Number.isFinite(count) && count > 0) {
      counts[key] = count;
    }
  }
  return counts;
}

function readCardReactionBody(body: unknown): CardReactionView {
  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    return { reaction_counts: {}, reactions: [] };
  }
  const record = body as Record<string, unknown>;
  const reactions = Array.isArray(record.reactions)
    ? record.reactions.filter((row): row is Reaction => {
        return !!row && typeof row === 'object' && typeof (row as Reaction).id === 'string';
      })
    : [];
  return {
    reaction_counts: readCountMap(record.reaction_counts),
    reactions,
  };
}

// Request deduplication: Track ongoing requests to prevent duplicates
const ongoingRequests = new Map<string, Promise<any>>();
const requestThrottle = new Map<string, number>();

// Helper function to manage request deduplication and throttling
const makeDeduplicatedRequest = async <T>(
  requestKey: string,
  requestFn: () => Promise<T>,
  throttleMs: number = 1000
): Promise<T> => {
  // Check if there's already an ongoing request for this key
  if (ongoingRequests.has(requestKey)) {
    logger.debug(LogCategory.REACTIONS, `DEDUP: Reusing ongoing request for ${requestKey}`);
    return ongoingRequests.get(requestKey)!;
  }

  // Check throttling
  const lastRequestTime = requestThrottle.get(requestKey) || 0;
  const now = Date.now();
  if (now - lastRequestTime < throttleMs) {
    logger.debug(LogCategory.REACTIONS, `THROTTLE: Request for ${requestKey} throttled (${throttleMs}ms)`);
    throw new Error(`Request throttled for ${requestKey}`);
  }

  // Create new request
  logger.debug(LogCategory.REACTIONS, `NEW_REQUEST: Starting request for ${requestKey}`);
  const requestPromise = requestFn()
    .finally(() => {
      // Clean up after request completes
      ongoingRequests.delete(requestKey);
      requestThrottle.set(requestKey, now);
    });

  // Store the promise to prevent duplicates
  ongoingRequests.set(requestKey, requestPromise);
  
  return requestPromise;
};

// Helper function to get current user ID from OIDC tokens
const getCurrentUserIdFromTokens = (): string | null => {
  try {
    const userInfo = localStorage.getItem('oidc_user_info');
    if (userInfo) {
      const parsed = JSON.parse(userInfo);
      return parsed.userId || null;
    }
    return null;
  } catch (error) {
    logger.authError('Error getting current user ID from OIDC tokens', error);
    return null;
  }
};

// Define event types in eventService.ts if not already present
// export const EVENTS = {
//   ...existing events,
//   REACTION_ADDED: 'REACTION_ADDED',
//   REACTION_DELETED: 'REACTION_DELETED',
//   REACTIONS_SYNCED: 'REACTIONS_SYNCED'
// };

class ReactionService {
  private cachedUserId: string | null = null; // Cache user ID for offline use
  private isInitialized: boolean = false;
  private syncInProgress: boolean = false; // Track if sync is currently running
  private queueProcessing: boolean = false; // Track queue processing state
  private queueBackoffMs: number = 1000; // base backoff
  private maxBackoffMs: number = 30000;
  
  // Request deduplication cache for ongoing getReactionsForHazard calls
  private ongoingHazardRequests: Map<string, Promise<Reaction[]>> = new Map();
  
  // Request deduplication cache for ongoing getReactionsForComment calls
  private ongoingCommentRequests: Map<string, Promise<Reaction[]>> = new Map();
  private batchSyncedCommentIds: Map<string, number> = new Map();
  private readonly BATCH_COMMENT_SYNC_TTL_MS = 10000;
  
  // Periodic sync management
  private periodicSyncInterval: NodeJS.Timeout | null = null;
  private displayedHazardIds: Set<string> = new Set();
  private lastPeriodicSync: number = 0;
  private readonly PERIODIC_SYNC_INTERVAL_MS = 30000; // 30 seconds
  private readonly MIN_SYNC_GAP_MS = 10000; // Minimum 10 seconds between syncs
  private cardRealtimeSeen: Set<string> = new Set();

  constructor() {
    // Set up online/offline event listeners
    this.setupConnectionListeners();
    // Initialize user cache
    this.initializeUserCache();
    // Set up websocket event listeners for real-time updates
    this.setupWebSocketListeners();

    // Kick off a delayed queue processing on startup
    setTimeout(() => {
      this.ensureQueuedForUnsynced().then(() => this.processSyncQueue());
    }, 1500);
  }

  // Set up event listeners for online/offline status
  private setupConnectionListeners(): void {
    window.addEventListener('online', () => {
      logger.reactionsInfo('Connection restored, triggering immediate sync');
      // First drain the durable queue, then perform regular sync
      this.processSyncQueue().finally(() => this.performSync());
      // Refresh user cache when online
      this.refreshUserCache();
    });

    window.addEventListener('offline', () => {
      logger.reactionsInfo('Connection lost, reactions will be queued for sync');
    });
  }

  // Set up websocket event listeners for real-time updates from other users
  private setupWebSocketListeners(): void {
    console.log('🔧 REACTION_SERVICE: Setting up WebSocket event listeners');
    // Listen for reaction events from other users via websocket
    eventEmitter.on(EVENTS.REACTION_ADDED, this.handleIncomingReactionAdded.bind(this));
    eventEmitter.on(EVENTS.REACTION_DELETED, this.handleIncomingReactionRemoved.bind(this));
    console.log('🔧 REACTION_SERVICE: WebSocket event listeners set up successfully');
  }

  // Handle incoming reaction addition events from websocket (when other users add reactions)
  private async handleIncomingReactionAdded(contentId: string, reactionData?: any): Promise<void> {
    try {
      logger.debug(LogCategory.REACTIONS, `Received reaction ADDED event for content ${contentId}`, reactionData);
      
      if (!reactionData) {
        logger.reactionsWarn(`No reaction data provided for content ${contentId}, cannot update state`);
        return;
      }

      // Determine content type from the reaction data
      const contentType = reactionData.content_type;
      if (!contentType || (contentType !== 'card' && contentType !== 'comment')) {
        logger.reactionsWarn(`Invalid or missing content_type in reaction data`, reactionData);
        return;
      }

      if (contentType === 'card') {
        await this.applyCardRealtime('add', contentId, reactionData);
        return;
      }

      logger.debug(LogCategory.REACTIONS, `Processing ${contentType} reaction ADDITION for ${contentId}`);

      // NOTE: We should NOT ignore events from the current user because:
      // 1. The user_id in the event is the ID of the user who added the reaction
      // 2. This could be the current user adding a reaction from another browser/tab
      // 3. We need to update the local database to reflect the addition
      
      console.log(`🟢 REACTION_ADDED: Processing reaction addition for user ${reactionData.user_id} (current user: ${await this.getCurrentUserId().catch(() => null)})`);

      // Convert server reaction data to our local Reaction format
      const serverReaction: Reaction = {
        id: reactionData.id || reactionData.server_id, // Use server-provided ID
        user_id: reactionData.user_id,
        reaction_type_name: reactionData.reaction_type_name,
        content_type: contentType,
        content_id: contentId,
        created_at: reactionData.created_at || new Date().toISOString(),
        deleted: false, // This is an addition event
        local_only: false, // This is from server, so not local only
        last_synced: new Date().toISOString()
      };

      // Check if reaction already exists locally
      const existingReactions = await reactionTable.filter(r => 
        r.content_type === contentType && 
        r.content_id === contentId &&
        r.user_id === reactionData.user_id &&
        r.reaction_type_name === reactionData.reaction_type_name &&
        !r.deleted
      ).toArray();

      if (existingReactions.length > 0) {
        // Reaction already exists locally, just update sync status
        logger.debug(LogCategory.REACTIONS, `Reaction already exists locally, updating sync status`);
        await reactionTable.update(existingReactions[0].id!, {
          last_synced: serverReaction.last_synced,
          local_only: false
        });
      } else {
        // New reaction from another user, add it to local database
        logger.debug(LogCategory.REACTIONS, `Adding new reaction from user ${reactionData.user_id}`);
        
        try {
          await reactionTable.add(serverReaction);
          logger.debug(LogCategory.REACTIONS, `Successfully added reaction ${serverReaction.id} for ${contentType} ${contentId}`);
        } catch (error: any) {
          // If there's a duplicate key error, try to update instead
          if (error.name === 'ConstraintError') {
            logger.debug(LogCategory.REACTIONS, `Reaction ${serverReaction.id} already exists, updating instead`);
            await reactionTable.update(serverReaction.id, {
              deleted: false,
              last_synced: serverReaction.last_synced,
              local_only: false
            });
          } else {
            throw error;
          }
        }
      }

      // Emit local event to update UI
      eventEmitter.emit(EVENTS.REACTIONS_SYNCED, contentId);
      logger.debug(LogCategory.REACTIONS, `Updated local reactions for ${contentType} ${contentId}`);
      
    } catch (error) {
      logger.reactionsWarn('Error handling incoming reaction added event', error);
    }
  }

  // Handle incoming reaction removal events from websocket (when other users remove reactions)
  private async handleIncomingReactionRemoved(contentId: string, reactionData?: any): Promise<void> {
    try {
      console.log(`🔴 REACTION_REMOVED: Received reaction REMOVED event for content ${contentId}`, reactionData);
      logger.debug(LogCategory.REACTIONS, `Received reaction REMOVED event for content ${contentId}`, reactionData);
      
      if (!reactionData) {
        console.log(`🔴 REACTION_REMOVED: No reaction data provided for content ${contentId}`);
        logger.reactionsWarn(`No reaction data provided for content ${contentId}, cannot update state`);
        return;
      }

      // Determine content type from the reaction data
      const contentType = reactionData.content_type;
      if (!contentType || (contentType !== 'card' && contentType !== 'comment')) {
        console.log(`🔴 REACTION_REMOVED: Invalid content_type: ${contentType}`);
        logger.reactionsWarn(`Invalid or missing content_type in reaction data`, reactionData);
        return;
      }

      if (contentType === 'card') {
        await this.applyCardRealtime('remove', contentId, reactionData);
        return;
      }

      console.log(`🔴 REACTION_REMOVED: Processing ${contentType} reaction REMOVAL for ${contentId}`);
      logger.debug(LogCategory.REACTIONS, `Processing ${contentType} reaction REMOVAL for ${contentId}`);

      // NOTE: We should NOT ignore events from the current user because:
      // 1. The user_id in the event is the ID of the user whose reaction was removed
      // 2. This could be the current user's reaction being removed by someone else
      // 3. Or it could be the current user removing their own reaction from another browser/tab
      // In both cases, we need to update the local database to reflect the removal
      
      console.log(`🔴 REACTION_REMOVED: Processing reaction removal for user ${reactionData.user_id} (current user: ${await this.getCurrentUserId().catch(() => null)})`);

      // Find and remove the reaction from local database
      console.log(`🔴 REACTION_REMOVED: Searching for reactions with:`, {
        contentType,
        contentId,
        userId: reactionData.user_id,
        reactionType: reactionData.reaction_type_name
      });
      
      // First, let's see all reactions for this content
      const allReactionsForContent = await reactionTable.filter(r => 
        r.content_type === contentType && 
        r.content_id === contentId
      ).toArray();
      
      console.log(`🔴 REACTION_REMOVED: All reactions for ${contentId}:`, allReactionsForContent);
      
      const existingReactions = await reactionTable.filter(r => 
        r.content_type === contentType && 
        r.content_id === contentId &&
        r.user_id === reactionData.user_id &&
        r.reaction_type_name === reactionData.reaction_type_name &&
        !r.deleted
      ).toArray();

      console.log(`🔴 REACTION_REMOVED: Found ${existingReactions.length} matching reactions:`, existingReactions);

      if (existingReactions.length > 0) {
        // Remove all matching reactions from local database
        console.log(`🔴 REACTION_REMOVED: Removing ${existingReactions.length} reactions from user ${reactionData.user_id}`);
        logger.debug(LogCategory.REACTIONS, `Removing ${existingReactions.length} reactions from user ${reactionData.user_id}`);
        
        for (const reaction of existingReactions) {
          await reactionTable.delete(reaction.id!);
          console.log(`🔴 REACTION_REMOVED: Deleted reaction ${reaction.id} for ${contentType} ${contentId}`);
          logger.debug(LogCategory.REACTIONS, `Deleted reaction ${reaction.id} for ${contentType} ${contentId}`);
        }
      } else {
        console.log(`🔴 REACTION_REMOVED: No matching reaction found to remove for user ${reactionData.user_id}`);
        logger.debug(LogCategory.REACTIONS, `No matching reaction found to remove for user ${reactionData.user_id}`);
      }

      // Emit local event to update UI
      console.log(`🔴 REACTION_REMOVED: Emitting REACTIONS_SYNCED event for ${contentId}`);
      eventEmitter.emit(EVENTS.REACTIONS_SYNCED, contentId);
      logger.debug(LogCategory.REACTIONS, `Updated local reactions for ${contentType} ${contentId}`);
      
    } catch (error) {
      console.error(`🔴 REACTION_REMOVED: Error handling incoming reaction removed event:`, error);
      logger.reactionsWarn('Error handling incoming reaction removed event', error);
    }
  }

  // Initialize user cache on startup
  private async initializeUserCache(): Promise<void> {
    // Get user ID from OIDC tokens
    const userId = getCurrentUserIdFromTokens();
    if (userId) {
      this.cachedUserId = userId;
      // Store in localStorage for offline use
      localStorage.setItem('safework_user_id', userId);
      logger.debug(LogCategory.AUTH, 'Loaded user ID from OIDC tokens for offline use');
    } else {
      // Try to load from localStorage if available
      const cachedUser = localStorage.getItem('safework_user_id');
      if (cachedUser) {
        this.cachedUserId = cachedUser;
        logger.debug(LogCategory.AUTH, 'Loaded cached user ID for offline use');
      }
    }
  }

  // Refresh user cache from OIDC tokens
  private async refreshUserCache(): Promise<void> {
    try {
      const userId = getCurrentUserIdFromTokens();
      
      if (userId) {
        this.cachedUserId = userId;
        // Store in localStorage for offline use
        localStorage.setItem('safework_user_id', userId);
        logger.debug(LogCategory.AUTH, 'User authentication cached for offline use');
      }
    } catch (error) {
      logger.authWarn('Failed to refresh user cache', error);
    }
  }

  // Get current user ID (with offline support)
  private async getCurrentUserId(): Promise<string> {
    // If we have a cached user ID, use it
    if (this.cachedUserId) {
      return this.cachedUserId;
    }

    // Check localStorage first for immediate response
    const cachedUser = localStorage.getItem('safework_user_id');
    if (cachedUser) {
      this.cachedUserId = cachedUser;
      return cachedUser;
    }

    // Try to get from OIDC tokens
    try {
      const userId = getCurrentUserIdFromTokens();
      
      if (userId) {
        this.cachedUserId = userId;
        localStorage.setItem('safework_user_id', userId);
        return userId;
      }
    } catch (error) {
      logger.authWarn('Failed to get user ID from OIDC tokens', error);
    }

    throw new Error('User not authenticated and no cached user ID available');
  }

  // Get reactions for a hazard with request deduplication
  async getReactionsForHazard(hazardId: string, shouldBackgroundSync: boolean = false): Promise<Reaction[]> {
    // Check if there's already an ongoing request for this hazard
    const ongoingRequest = this.ongoingHazardRequests.get(hazardId);
    if (ongoingRequest) {
      logger.debug(LogCategory.REACTIONS, `Reusing ongoing request for hazard ${hazardId}`);
      return ongoingRequest;
    }

    // Create new request and cache it
    const requestPromise = this.fetchHazardReactionsInternal(hazardId, shouldBackgroundSync);
    this.ongoingHazardRequests.set(hazardId, requestPromise);

    try {
      const result = await requestPromise;
      return result;
    } finally {
      // Clean up the cache when request completes (success or failure)
      this.ongoingHazardRequests.delete(hazardId);
    }
  }

  // Internal method that actually fetches the reactions - LOCAL-FIRST APPROACH
  private async fetchHazardReactionsInternal(hazardId: string, shouldBackgroundSync: boolean = false): Promise<Reaction[]> {
    try {
      logger.debug(LogCategory.REACTIONS, `Starting LOCAL-FIRST fetch for hazard ${hazardId}`);
      
      // STEP 1: Current user's rows only. Counts live on the hazard.
      const currentUserId = await this.getCurrentUserId().catch(() => '');
      const localReactions = (await reactionTable.filter(r => 
        r.content_type === 'card' && 
        r.content_id === hazardId &&
        !r.deleted
      ).toArray()).filter(row => !currentUserId || row.user_id === currentUserId);

      logger.debug(LogCategory.REACTIONS, `Found ${localReactions.length} local reactions`, localReactions.map(r => ({
        id: r.id,
        user_id: r.user_id,
        reaction_type: r.reaction_type_name,
        deleted: r.deleted,
        local_only: r.local_only,
        last_synced: r.last_synced
      })));

      // STEP 2: If online, optionally sync server data in background (non-blocking)
      if (navigator.onLine && shouldBackgroundSync) {
        // Use deduplicated request to prevent multiple simultaneous requests for same hazard
        const backgroundSyncKey = `background_sync_${hazardId}`;
        
        setTimeout(async () => {
          try {
            await makeDeduplicatedRequest(
              backgroundSyncKey,
              async () => {
                logger.debug(LogCategory.REACTIONS, `BACKGROUND_SYNC: Fetching server reactions for hazard ${hazardId}`);
                const response = await fetchWithAuth(`${API_BASE_URL}/api/getHazardReactions?hazardId=${hazardId}`);
                if (response.ok) {
                  const view = readCardReactionBody(await response.json());
                  logger.debug(LogCategory.REACTIONS, `BACKGROUND_SYNC: Found ${view.reactions.length} server reactions, syncing to local DB`);
                  await this.applyCardReactionSync(hazardId, view);
                  eventEmitter.emit(EVENTS.REACTIONS_SYNCED, hazardId);
                  
                  logger.debug(LogCategory.REACTIONS, `BACKGROUND_SYNC: Completed background sync for hazard ${hazardId}`);
                } else {
                  console.warn(`🔄 BACKGROUND_SYNC: Server returned ${response.status} for hazard reactions`);
                }
              },
              2000 // Throttle background sync requests to max once per 2 seconds per hazard
            );
          } catch (error) {
            if (error instanceof Error && error.message.includes('throttled')) {
              logger.debug(LogCategory.REACTIONS, `BACKGROUND_SYNC: Request for hazard ${hazardId} was throttled`);
            } else {
              console.warn('🔄 BACKGROUND_SYNC: Failed to sync reactions from server:', error);
            }
          }
        }, 100); // Small delay to ensure UI renders first
      }

      // STEP 3: Return local data immediately
      return localReactions;
    } catch (error) {
      console.error('Error getting reactions for hazard:', error);
      throw error;
    }
  }

  // Get reactions for a comment with request deduplication
  async getReactionsForComment(commentId: string, shouldBackgroundSync: boolean = false): Promise<Reaction[]> {
    // Check if there's already an ongoing request for this comment
    const ongoingRequest = this.ongoingCommentRequests.get(commentId);
    if (ongoingRequest) {
      logger.debug(LogCategory.REACTIONS, `GET_COMMENT_REACTIONS: Reusing ongoing request for comment ${commentId}`);
      return ongoingRequest;
    }

    // Create new request and cache it
    const requestPromise = this.fetchCommentReactionsInternal(commentId, shouldBackgroundSync);
    this.ongoingCommentRequests.set(commentId, requestPromise);

    try {
      const result = await requestPromise;
      return result;
    } finally {
      // Clean up the cache when request completes (success or failure)
      this.ongoingCommentRequests.delete(commentId);
    }
  }

  // Internal method for fetching comment reactions - LOCAL-FIRST APPROACH
  private async fetchCommentReactionsInternal(commentId: string, shouldBackgroundSync: boolean = false): Promise<Reaction[]> {
    try {
      logger.debug(LogCategory.REACTIONS, `GET_COMMENT_REACTIONS: Starting LOCAL-FIRST fetch for comment ${commentId}`);
      
      // STEP 1: Always get local reactions first (immediate response)
      const localReactions = await reactionTable
        .filter(r => 
          !r.deleted && 
          r.content_type === 'comment' && 
          r.content_id === commentId
        )
        .toArray();
        
      logger.debug(LogCategory.REACTIONS, `GET_COMMENT_REACTIONS: Found ${localReactions.length} local reactions for comment ${commentId}`);
      
      // STEP 2: If online, optionally sync server data in background (non-blocking)
      if (navigator.onLine && shouldBackgroundSync && !this.isCommentBatchSyncedRecently(commentId)) {
        // Make server request in background without waiting for it
        setTimeout(async () => {
          if (this.isCommentBatchSyncedRecently(commentId)) {
            return;
          }
          try {
            logger.debug(LogCategory.REACTIONS, `BACKGROUND_SYNC: Fetching server reactions for comment ${commentId}`);
            const response = await fetchWithAuth(`${API_BASE_URL}/api/getCommentReactions?commentId=${commentId}`);
            if (response.ok) {
              const serverReactions: Reaction[] = await response.json();
              
              logger.debug(LogCategory.REACTIONS, `BACKGROUND_SYNC: Found ${serverReactions.length} server reactions, syncing to local DB`);
              
              // Update local database with all reactions in background
              await this.syncReactionsToLocal(serverReactions);
              
              // Emit event to notify UI components that data may have changed
              eventEmitter.emit(EVENTS.REACTIONS_SYNCED, commentId);
              
              logger.debug(LogCategory.REACTIONS, `BACKGROUND_SYNC: Completed background sync for comment ${commentId}`);
            } else {
              console.warn(`🔄 BACKGROUND_SYNC: Server returned ${response.status} for comment reactions`);
            }
          } catch (error) {
            console.warn('🔄 BACKGROUND_SYNC: Failed to sync comment reactions from server:', error);
          }
        }, 0);
      }
      
      // STEP 3: Return local data immediately
      return localReactions;
    } catch (error) {
      console.error('Error getting comment reactions:', error);
      return [];
    }
  }

  // OPTIMIZED: Add a reaction to a hazard - FAST UI-first approach
  async addHazardReaction(hazardId: string, reactionType: string): Promise<void> {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }

      // STEP 1: Quick check for existing reaction (toggle behavior)
      const existingReaction = await reactionTable
        .where(['user_id', 'reaction_type_name', 'content_type', 'content_id'])
        .equals([userId, reactionType, 'card', hazardId])
        .filter(r => !r.deleted)
        .first();

      if (existingReaction) {
        // Toggle off - remove existing reaction
        return this.removeHazardReaction(hazardId, reactionType);
      }

      // STEP 1: Perform action - Create reaction object
      const reaction: Reaction = {
        id: crypto.randomUUID().toUpperCase(),
        user_id: userId,
        reaction_type_name: reactionType,
        content_type: 'card',
        content_id: hazardId,
        created_at: moment().format(),
        local_only: true,
        last_synced: undefined
      };

      // STEP 1: Store locally (single fast operation)
      await reactionTable.add(reaction);

      // STEP 2: Update UI immediately (synchronous)
      const reactionData = {
        id: reaction.id,
        user_id: reaction.user_id,
        reaction_type_name: reaction.reaction_type_name,
        content_type: 'card',
        content_id: hazardId,
        created_at: reaction.created_at
      };
      eventEmitter.emit(EVENTS.REACTION_ADDED, hazardId, reactionData);

      // STEP 3: Enqueue durable operation and process in background
      await this.enqueueReactionOperation(reaction, 'CREATE');
      this.processSyncQueue();

      // STEP 4: Validate sync integrity if online
      this.validateReactionSyncIntegrity('card', hazardId);

      // STEP 5: End response
      console.log(`✅ Reaction added with ID: ${reaction.id}`);
      
    } catch (error) {
      console.error('Error adding hazard reaction:', error);
      throw error;
    }
  }

  // OPTIMIZED: Remove a reaction from a hazard - FAST UI-first approach
  async removeHazardReaction(hazardId: string, reactionType: string): Promise<void> {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }

      // STEP 1: Find existing reaction
      const reaction = await reactionTable
        .where(['user_id', 'reaction_type_name', 'content_type', 'content_id'])
        .equals([userId, reactionType, 'card', hazardId])
        .filter(r => !r.deleted)
        .first();

      if (!reaction) {
        console.warn(`No reaction found to remove for hazard ${hazardId}`);
        return;
      }

      // STEP 1: Mark as tombstone instead of deleting to ensure reliable sync
      await reactionTable.update(reaction.id!, {
        deleted: true,
        local_only: true,
        last_synced: undefined
      });

      // STEP 2: Update UI immediately (synchronous)
      const reactionData = {
        id: reaction.id,
        user_id: reaction.user_id,
        reaction_type_name: reaction.reaction_type_name,
        content_type: 'card',
        content_id: hazardId
      };
      eventEmitter.emit(EVENTS.REACTION_DELETED, hazardId, reactionData);

      // STEP 3: Enqueue durable operation and process in background
      await this.enqueueReactionOperation({ ...reaction, deleted: true, local_only: true, last_synced: undefined }, 'DELETE');
      this.processSyncQueue();

      // STEP 4: Validate sync integrity if online
      this.validateReactionSyncIntegrity('card', hazardId);

      // STEP 5: End response
      console.log(`✅ Reaction removed with ID: ${reaction.id}`);
      
    } catch (error) {
      console.error('Error removing hazard reaction:', error);
      throw error;
    }
  }

  // OPTIMIZED: Add a reaction to a comment - FAST UI-first approach
  async addCommentReaction(commentId: string, reactionType: string): Promise<void> {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }

      // STEP 1: Quick check for existing reaction (toggle behavior)
      const existingReaction = await reactionTable
        .where(['user_id', 'reaction_type_name', 'content_type', 'content_id'])
        .equals([userId, reactionType, 'comment', commentId])
        .filter(r => !r.deleted)
        .first();

      if (existingReaction) {
        // Toggle off - remove existing reaction
        return this.removeCommentReaction(commentId, reactionType);
      }

      // STEP 1: Perform action - Create reaction object
      const reaction: Reaction = {
        id: crypto.randomUUID().toUpperCase(),
        user_id: userId,
        reaction_type_name: reactionType,
        content_type: 'comment',
        content_id: commentId,
        created_at: moment().format(),
        local_only: true,
        last_synced: undefined
      };

      // STEP 1: Store locally (single fast operation)
      await reactionTable.add(reaction);

      // STEP 2: Update UI immediately (synchronous)
      const reactionData = {
        id: reaction.id,
        user_id: reaction.user_id,
        reaction_type_name: reaction.reaction_type_name,
        content_type: 'comment',
        content_id: commentId,
        created_at: reaction.created_at
      };
      eventEmitter.emit(EVENTS.REACTION_ADDED, commentId, reactionData);

      // STEP 3: Enqueue durable operation and process in background
      await this.enqueueReactionOperation(reaction, 'CREATE');
      this.processSyncQueue();

      // STEP 4: Validate sync integrity if online
      this.validateReactionSyncIntegrity('comment', commentId);

      // STEP 5: End response
      console.log(`✅ Comment reaction added with ID: ${reaction.id}`);
      
    } catch (error) {
      console.error('Error adding comment reaction:', error);
      throw error;
    }
  }

  // OPTIMIZED: Remove a comment reaction - FAST UI-first approach
  async removeCommentReaction(commentId: string, reactionType: string): Promise<void> {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }

      // STEP 1: Find existing reaction
      const reaction = await reactionTable
        .where(['user_id', 'reaction_type_name', 'content_type', 'content_id'])
        .equals([userId, reactionType, 'comment', commentId])
        .filter(r => !r.deleted)
        .first();

      if (!reaction) {
        console.warn(`No comment reaction found to remove for comment ${commentId}`);
        return;
      }

      // STEP 1: Mark as tombstone instead of deleting to ensure reliable sync
      await reactionTable.update(reaction.id!, {
        deleted: true,
        local_only: true,
        last_synced: undefined
      });

      // STEP 2: Update UI immediately (synchronous)
      const reactionData = {
        id: reaction.id,
        user_id: reaction.user_id,
        reaction_type_name: reaction.reaction_type_name,
        content_type: 'comment',
        content_id: commentId
      };
      eventEmitter.emit(EVENTS.REACTION_DELETED, commentId, reactionData);

      // STEP 3: Enqueue durable operation and process in background
      await this.enqueueReactionOperation({ ...reaction, deleted: true, local_only: true, last_synced: undefined }, 'DELETE');
      this.processSyncQueue();

      // STEP 4: Validate sync integrity if online
      this.validateReactionSyncIntegrity('comment', commentId);

      // STEP 5: End response
      console.log(`✅ Comment reaction removed with ID: ${reaction.id}`);
      
    } catch (error) {
      console.error('Error removing comment reaction:', error);
      throw error;
    }
  }

  // ============ Reaction Sync Validation ============
  
  /**
   * Validates the integrity of local reactions by comparing them with the server state.
   * If online, fetches reactions from server and removes any local reactions that don't match.
   */
  private async validateReactionSyncIntegrity(contentType: 'card' | 'comment', contentId: string): Promise<void> {
    // Run validation in background to avoid blocking the UI
    setTimeout(async () => {
      try {
        // Check if we're really online by testing connectivity
        const isOnline = await this.checkConnectivity();
        if (!isOnline) {
          console.log(`🔍 SYNC_VALIDATION: Skipping validation for ${contentType} ${contentId} - device is offline`);
          return;
        }

        console.log(`🔍 SYNC_VALIDATION: Starting validation for ${contentType} ${contentId}`);

        // Fetch server reactions for this content
        const serverReactions = await this.fetchServerReactionsForContent(contentType, contentId);
        
        // Get current local reactions for comparison
        const localReactions = await reactionTable
          .filter(r => 
            r.content_type === contentType && 
            r.content_id === contentId &&
            !r.deleted // Only compare non-deleted reactions
          )
          .toArray();

        // Compare and clean up mismatched reactions
        await this.compareAndCleanupReactions(contentType, contentId, serverReactions, localReactions);

        console.log(`✅ SYNC_VALIDATION: Completed validation for ${contentType} ${contentId}`);
        
      } catch (error) {
        console.warn(`⚠️ SYNC_VALIDATION: Failed to validate reactions for ${contentType} ${contentId}:`, error);
      }
    }, 2000); // 2 second delay to allow server processing time
  }

  /**
   * Tests actual connectivity to the server by making a lightweight request
   */
  private async checkConnectivity(): Promise<boolean> {
    try {
      // Use the health check endpoint to test connectivity
      const response = await fetch(`${API_BASE_URL}/api/db-health-check`, {
        method: 'GET',
        headers:
          typeof window !== 'undefined'
            ? { 'X-Forwarded-Host': window.location.host }
            : undefined,
        signal: AbortSignal.timeout(3000) // 3 second timeout
      });
      return response.ok;
    } catch (error) {
      console.log('🔍 CONNECTIVITY_TEST: Failed:', error);
      return false;
    }
  }

  private rememberCardEvent(key: string): boolean {
    if (this.cardRealtimeSeen.has(key)) {
      return false;
    }
    this.cardRealtimeSeen.add(key);
    if (this.cardRealtimeSeen.size > 400) {
      const oldest = this.cardRealtimeSeen.values().next().value;
      if (oldest) {
        this.cardRealtimeSeen.delete(oldest);
      }
    }
    return true;
  }

  private async bumpHazardReactionCount(hazardId: string, typeName: string, delta: number): Promise<void> {
    if (!typeName || !Number.isFinite(delta) || delta === 0) {
      return;
    }
    const hazard = await hazardGateway.get(hazardId);
    if (!hazard) {
      return;
    }
    const counts = readCountMap(hazard.reaction_counts);
    const next = (counts[typeName] ?? 0) + delta;
    if (next > 0) {
      counts[typeName] = next;
    } else {
      delete counts[typeName];
    }
    await hazardGateway.bulkPut([{ ...hazard, reaction_counts: counts }]);
  }

  private async applyCardReactionSync(hazardId: string, view: CardReactionView): Promise<void> {
    const hazard = await hazardGateway.get(hazardId);
    if (hazard) {
      await hazardGateway.bulkPut([{ ...hazard, reaction_counts: view.reaction_counts }]);
    }
    const currentUserId = await this.getCurrentUserId().catch(() => '');
    const mine = view.reactions.filter(row => !currentUserId || !row.user_id || row.user_id === currentUserId);
    const keep = new Set(mine.map(row => row.id));
    const existing = await reactionTable
      .filter(row => row.content_type === 'card' && row.content_id === hazardId && !row.local_only)
      .toArray();
    for (const row of existing) {
      if (!keep.has(row.id)) {
        await reactionTable.delete(row.id);
      }
    }
    const syncedAt = moment().format();
    for (const reaction of mine) {
      await reactionTable.put({
        id: reaction.id,
        user_id: reaction.user_id,
        reaction_type_name: reaction.reaction_type_name,
        content_type: 'card',
        content_id: hazardId,
        created_at: reaction.created_at || syncedAt,
        last_synced: syncedAt,
        local_only: false,
        deleted: false,
      });
    }
  }

  private async applyCardRealtime(kind: 'add' | 'remove', contentId: string, reactionData: Record<string, unknown>): Promise<void> {
    const typeName = typeof reactionData.reaction_type_name === 'string' ? reactionData.reaction_type_name : '';
    const reactionId = typeof reactionData.id === 'string' ? reactionData.id : '';
    const userId = typeof reactionData.user_id === 'string' ? reactionData.user_id : '';
    const key = `${kind}:${contentId}:${reactionId}:${userId}:${typeName}`;
    if (!this.rememberCardEvent(key)) {
      return;
    }
    const currentUserId = await this.getCurrentUserId().catch(() => '');
    if (currentUserId && userId === currentUserId && reactionId) {
      if (kind === 'remove') {
        await reactionTable.delete(reactionId);
      } else if (!(await reactionTable.get(reactionId))) {
        try {
          await reactionTable.add({
            id: reactionId,
            user_id: userId,
            reaction_type_name: typeName,
            content_type: 'card',
            content_id: contentId,
            created_at: typeof reactionData.created_at === 'string' ? reactionData.created_at : new Date().toISOString(),
            deleted: false,
            local_only: false,
            last_synced: new Date().toISOString(),
          });
        } catch (error: unknown) {
          const name = error && typeof error === 'object' && 'name' in error
            ? String((error as { name?: string }).name)
            : '';
          if (name !== 'ConstraintError') {
            throw error;
          }
        }
      }
    }
    await this.bumpHazardReactionCount(contentId, typeName, kind === 'add' ? 1 : -1);
    eventEmitter.emit(EVENTS.REACTIONS_SYNCED, contentId);
  }

  /**
   * Fetches reactions from the server for a specific content item
   */
  private async fetchServerReactionsForContent(contentType: 'card' | 'comment', contentId: string): Promise<Reaction[]> {
    try {
      let response: Response;
      
      if (contentType === 'card') {
        response = await fetchWithAuth(`${API_BASE_URL}/api/getHazardReactions?hazardId=${contentId}`, { 
          signal: AbortSignal.timeout(5000) // 5 second timeout
        });
        if (!response.ok) {
          throw new Error(`Server returned ${response.status}`);
        }
        const view = readCardReactionBody(await response.json());
        await this.applyCardReactionSync(contentId, view);
        return view.reactions;
      } else {
        response = await fetchWithAuth(`${API_BASE_URL}/api/getCommentReactions?commentId=${contentId}`, { 
          signal: AbortSignal.timeout(5000) // 5 second timeout
        });
      }

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

      const serverReactions: Reaction[] = await response.json();
      console.log(`📡 SERVER_FETCH: Retrieved ${serverReactions.length} reactions for ${contentType} ${contentId}`);
      
      return serverReactions;
    } catch (error) {
      console.error(`❌ SERVER_FETCH: Failed to fetch server reactions for ${contentType} ${contentId}:`, error);
      throw error;
    }
  }

  /**
   * Compares server and local reactions, removing any local reactions that don't exist on the server
   */
  private async compareAndCleanupReactions(
    contentType: 'card' | 'comment',
    contentId: string,
    serverReactions: Reaction[],
    localReactions: Reaction[]
  ): Promise<void> {
    try {
      // Create a set of server reaction identifiers for fast lookup
      const serverReactionSet = new Set();
      serverReactions.forEach(reaction => {
        // Create a unique identifier for each reaction
        const identifier = `${reaction.user_id}:${reaction.reaction_type_name}:${reaction.content_type}:${reaction.content_id}`;
        serverReactionSet.add(identifier);
      });

      // Find local reactions that don't exist on the server
      const mismatchedReactions: Reaction[] = [];
      localReactions.forEach(localReaction => {
        if (contentType === 'card' && localReaction.local_only) {
          return;
        }
        const localIdentifier = `${localReaction.user_id}:${localReaction.reaction_type_name}:${localReaction.content_type}:${localReaction.content_id}`;
        
        if (!serverReactionSet.has(localIdentifier)) {
          // This local reaction doesn't exist on the server
          mismatchedReactions.push(localReaction);
        }
      });

      if (mismatchedReactions.length > 0) {
        console.warn(`⚠️ SYNC_MISMATCH: Found ${mismatchedReactions.length} local reactions that don't exist on server for ${contentType} ${contentId}`);
        
        // Remove mismatched reactions from local database
        for (const mismatchedReaction of mismatchedReactions) {
          console.log(`🗑️ CLEANUP: Removing mismatched local reaction ${mismatchedReaction.id} (${mismatchedReaction.reaction_type_name} by ${mismatchedReaction.user_id})`);
          
          await reactionTable.delete(mismatchedReaction.id);
          
          // Also remove from sync queue if it exists there
          await hazardGateway.dropOpForEntity(mismatchedReaction.id);
        }

        // Emit event to refresh UI
        eventEmitter.emit(EVENTS.REACTIONS_SYNCED, contentId);
        
        console.log(`✅ CLEANUP: Removed ${mismatchedReactions.length} mismatched reactions for ${contentType} ${contentId}`);
      } else {
        console.log(`✅ SYNC_MATCH: All local reactions match server state for ${contentType} ${contentId}`);
      }
      
    } catch (error) {
      console.error(`❌ COMPARE_CLEANUP: Failed to compare and cleanup reactions for ${contentType} ${contentId}:`, error);
      throw error;
    }
  }

  // ============ Durable Queue Implementation ============
  
  // HTTP fallback methods for reaction operations
  private async httpAddReaction(reaction: Reaction): Promise<void> {
    const endpoint = reaction.content_type === 'card' ? `${API_BASE_URL}/api/addHazardReaction` : `${API_BASE_URL}/api/addCommentReaction`;
    const payload = {
      [reaction.content_type === 'card' ? 'hazardId' : 'commentId']: reaction.content_id,
      reactionType: reaction.reaction_type_name,
      id: reaction.id
    };

    const response = await fetchWithAuth(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: { message: 'Unknown error' } }));
      throw new Error(`HTTP ${response.status}: ${errorData.error?.message || 'Failed to add reaction'}`);
    }
    
    // For comment reactions, the server should return the reaction ID
    if (reaction.content_type === 'comment') {
      try {
        const responseData = await response.json();
        if (responseData?.reactionId && responseData.reactionId !== reaction.id) {
          // Update local reaction with server-provided ID if different
          await reactionTable.update(reaction.id!, { id: responseData.reactionId });
        }
      } catch (e) {
        console.warn(`Could not parse response for comment reaction ${reaction.id}:`, e);
      }
    }
  }

  private async httpRemoveReaction(reaction: Reaction): Promise<void> {
    const endpoint = reaction.content_type === 'card' ? `${API_BASE_URL}/api/removeHazardReaction` : `${API_BASE_URL}/api/removeCommentReaction`;
    const payload = reaction.content_type === 'card' ? {
      hazardId: reaction.content_id,
      reactionType: reaction.reaction_type_name,
      id: reaction.id
    } : {
      commentId: reaction.content_id,
      reactionType: reaction.reaction_type_name,
      reactionId: reaction.id // Note: different field name for comment reactions
    };

    const response = await fetchWithAuth(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: { message: 'Unknown error' } }));
      throw new Error(`HTTP ${response.status}: ${errorData.error?.message || 'Failed to remove reaction'}`);
    }
  }

  // Sync reactions from server to local DB
  private async syncReactionsToLocal(reactions: Reaction[]): Promise<void> {
    try {
      const reactionsByContent = new Map<string, Reaction[]>();
        
        for (const reaction of reactions) {
          const contentId = reaction.content_id;
          if (!reactionsByContent.has(contentId)) {
            reactionsByContent.set(contentId, []);
          }
          reactionsByContent.get(contentId)!.push(reaction);
        }
        
        // For each content (hazard/comment), sync its reactions
        const contentIds = Array.from(reactionsByContent.keys());
        for (const contentId of contentIds) {
          const contentReactions = reactionsByContent.get(contentId)!;
          // Get the content type from the first reaction
          const contentType = contentReactions[0]?.content_type || 'card';
          
          // CRITICAL FIX: Clear existing server-synced reactions for this content
          // This ensures that if server returns 0 reactions, local DB is also cleared
          await reactionTable
            .filter(r => r.content_type === contentType && r.content_id === contentId && !r.local_only)
            .delete();
          
          console.log(`🔄 SYNC: Cleared existing server-synced reactions for ${contentType} ${contentId}`);
          
          // Now add the current server reactions
          for (const reaction of contentReactions) {
            // Handle server response format - map server_id to id if present
            const withServerId = reaction as Reaction & { server_id?: string };
            const serverId = withServerId.server_id || reaction.id;
            const reactionToStore: Reaction = {
              id: serverId,
              user_id: reaction.user_id,
              reaction_type_name: reaction.reaction_type_name,
              content_type: reaction.content_type,
              content_id: reaction.content_id,
              created_at: reaction.created_at,
              last_synced: moment().format(),
              local_only: false, // This came from server
              deleted: reaction.deleted || false
            };

            // Add reaction from server
            await reactionTable.put(reactionToStore);
          }
          
          console.log(`🔄 SYNC: Synced ${contentReactions.length} server reactions for ${contentType} ${contentId}`);
        }
    } catch (error) {
      console.error('Error syncing reactions to local DB:', error);
      throw error;
    }
  }

  // ============ Durable Queue Implementation ============
  private async enqueueReactionOperation(reaction: Reaction, operation: 'CREATE' | 'DELETE'): Promise<void> {
    const parent = await hazardGateway.findOpByEntity(reaction.content_id);
    const dependsOn = parent && (parent.kind === 'hazard.create' || parent.kind === 'comment.create')
      ? parent.id
      : undefined;
    await hazardGateway.enqueueUserOp({
      kind: operation === 'DELETE' ? 'reaction.remove' : 'reaction.add',
      entityId: reaction.id,
      payload: {
        id: reaction.id,
        user_id: reaction.user_id,
        reaction_type_name: reaction.reaction_type_name,
        content_type: reaction.content_type,
        content_id: reaction.content_id,
      },
      dependsOn,
    });
  }

  private async ensureQueuedForUnsynced(): Promise<void> {
    const unsynced = await reactionTable
      .filter(r => r.local_only === true)
      .toArray();

    for (const r of unsynced) {
      const exists = await hazardGateway.findOpByEntity(r.id);
      if (!exists) {
        await this.enqueueReactionOperation(r, r.deleted ? 'DELETE' : 'CREATE');
      }
    }
  }

  private async processSyncQueue(): Promise<void> {
    const { drainOutbox } = await import('./outboxDrain');
    await drainOutbox();
  }
  // Batch load reactions for multiple hazards - LOCAL-FIRST APPROACH
  async getBatchHazardReactions(hazardIds: string[]): Promise<Record<string, Reaction[]>> {
    if (!hazardIds.length) return {};
    
    try {
      console.log(`🔍 BATCH_HAZARD: Starting LOCAL-FIRST batch fetch for ${hazardIds.length} hazards`);
      
      // STEP 1: Always get local data first (immediate response)
      const currentUserId = await this.getCurrentUserId().catch(() => '');
      const allReactions = (await reactionTable.filter(r => 
        !r.deleted && 
        r.content_type === 'card' &&
        hazardIds.includes(r.content_id as string)
      ).toArray()).filter(row => !currentUserId || row.user_id === currentUserId);
      
      // Group by content_id
      const result: Record<string, Reaction[]> = {};
      for (const reaction of allReactions) {
        const id = reaction.content_id as string;
        if (!result[id]) {
          result[id] = [];
        }
        result[id].push(reaction);
      }
      
      console.log(`🔍 BATCH_HAZARD: Returning ${Object.keys(result).length} hazards with reactions immediately`);
      
      // STEP 2: If online, fetch from server in background (non-blocking)
      if (navigator.onLine) {
        setTimeout(async () => {
          try {
            console.log(`🔄 BACKGROUND_BATCH: Fetching batch reactions for ${hazardIds.length} hazards`);
    const response = await fetchWithAuth(`${API_BASE_URL}/api/getBatchHazardReactions?ids=${hazardIds.join(',')}`);

            if (response.ok) {
              const data = await response.json();
              const items = Array.isArray(data) ? data : [];
              for (const item of items) {
                if (!item || typeof item !== 'object') {
                  continue;
                }
                const hazardId = typeof item.hazardId === 'string' ? item.hazardId : '';
                if (!hazardId) {
                  continue;
                }
                await this.applyCardReactionSync(hazardId, readCardReactionBody(item));
                eventEmitter.emit(EVENTS.REACTIONS_SYNCED, hazardId);
              }
              
              console.log(`🔄 BACKGROUND_BATCH: Completed background batch sync for hazards`);
            } else {
              console.warn(`🔄 BACKGROUND_BATCH: Server returned ${response.status} for batch hazard reactions`);
            }
          } catch (error) {
            console.warn('🔄 BACKGROUND_BATCH: Failed to fetch batch reactions from server:', error);
          }
        }, 0);
      }
      
      return result;
    } catch (error) {
      console.error('Error batch loading hazard reactions:', error);
      return {};
    }
  }

  private isCommentBatchSyncedRecently(commentId: string): boolean {
    const lastBatchAt = this.batchSyncedCommentIds.get(commentId);
    if (!lastBatchAt) {
      return false;
    }
    if (Date.now() - lastBatchAt > this.BATCH_COMMENT_SYNC_TTL_MS) {
      this.batchSyncedCommentIds.delete(commentId);
      return false;
    }
    return true;
  }

  // Get batch reactions for comments - LOCAL-FIRST APPROACH
  async getBatchCommentReactions(commentIds: string[]): Promise<Record<string, Reaction[]>> {
    if (!commentIds.length) return {};
    
    try {
      logger.debug(LogCategory.REACTIONS, `BATCH_COMMENT: Starting LOCAL-FIRST batch fetch for ${commentIds.length} comments`);
      if (navigator.onLine) {
        const now = Date.now();
        for (const id of commentIds) {
          this.batchSyncedCommentIds.set(id, now);
        }
      }
      
      // STEP 1: Always get local data first (immediate response)
      const allReactions = await reactionTable
        .filter(r => 
          r.content_type === 'comment' && 
          !r.deleted &&
          commentIds.includes(r.content_id as string)
        )
        .toArray();
      
      // Group by content_id
      const result: Record<string, Reaction[]> = {};
      for (const reaction of allReactions) {
        const id = reaction.content_id;
        if (!result[id]) {
          result[id] = [];
        }
        result[id].push(reaction);
      }
      
      logger.debug(LogCategory.REACTIONS, `BATCH_COMMENT: Returning ${Object.keys(result).length} comments with reactions immediately`);
      
      // STEP 2: If online, fetch from server in background (non-blocking)
      if (navigator.onLine) {
        setTimeout(async () => {
          try {
            logger.debug(LogCategory.REACTIONS, `BACKGROUND_BATCH: Fetching batch reactions for ${commentIds.length} comments`);
            const response = await fetchWithAuth(`${API_BASE_URL}/api/getBatchCommentReactions?ids=${commentIds.join(',')}`);

            if (response.ok) {
              const data = await response.json();
              
              logger.debug(LogCategory.REACTIONS, `BACKGROUND_BATCH: Syncing ${data.length} comment reaction batches to local DB`);
              
              // Update local database with the fetched reactions
              await this.syncReactionsToLocal(data.flatMap((item: any) => item.reactions || []));
              for (const item of data) {
                if (item?.commentId && (!Array.isArray(item.reactions) || item.reactions.length === 0)) {
                  await reactionTable
                    .filter((r) => r.content_type === 'comment' && r.content_id === item.commentId && !r.local_only)
                    .delete();
                }
              }
              
              const completedAt = Date.now();
              // Emit events for each comment to update UI
              for (const item of data) {
                if (item?.commentId) {
                  this.batchSyncedCommentIds.set(item.commentId, completedAt);
                }
                eventEmitter.emit(EVENTS.REACTIONS_SYNCED, item.commentId);
              }
              
              logger.debug(LogCategory.REACTIONS, `BACKGROUND_BATCH: Completed background batch sync for comments`);
            } else {
              console.warn(`🔄 BACKGROUND_BATCH: Server returned ${response.status} for batch comment reactions`);
            }
          } catch (error) {
            console.warn('🔄 BACKGROUND_BATCH: Failed to fetch batch comment reactions from server:', error);
          }
        }, 0);
      }
      
      return result;
    } catch (error) {
      console.error('Error getting batch comment reactions:', error);
      return {};
    }
  }

  // Public method to manually trigger sync
  async forcSync(): Promise<void> {
    await this.performSync();
  }

  // Public method to manually cleanup local-only deleted reactions
  async cleanupOfflineReactions(): Promise<void> {
    await this.cleanupLocalOnlyDeletedReactions();
  }

  // Check if there are unsynced reactions
  async hasUnsyncedReactions(): Promise<boolean> {
    const unsyncedCount = await reactionTable.filter(r => r.local_only === true).count();
    return unsyncedCount > 0;
  }

  // Get count of unsynced reactions
  async getUnsyncedReactionCount(): Promise<number> {
    const unsyncedCount = await reactionTable.filter(r => r.local_only === true).count();
    return unsyncedCount;
  }

  // Check if sync is currently in progress
  isSyncInProgress(): boolean {
    return this.syncInProgress;
  }

  // Push unsynced reactions to server (legacy method - now uses backgroundSync)
  async pushUnsyncedReactions(): Promise<void> {
    await this.ensureQueuedForUnsynced();
    await this.processSyncQueue();
    await this.performSync();
  }

  // Background sync method
  private async performSync(): Promise<void> {
    if (this.syncInProgress || !navigator.onLine) {
      return;
    }

    this.syncInProgress = true;
    console.log('Starting background sync of reactions...');

    try {
      // First process durable queue
      await this.processSyncQueue();

      // Clean up reactions that were created and deleted locally
      await this.cleanupLocalOnlyDeletedReactions();

      // Enqueue any remaining unsynced reactions and let the queue drain
      const unsyncedReactions = await reactionTable
        .filter(r => r.local_only === true)
        .toArray();

      for (const reaction of unsyncedReactions) {
        await this.enqueueReactionOperation(reaction, reaction.deleted ? 'DELETE' : 'CREATE');
      }

      await this.processSyncQueue();

      // Notify that sync completed
      eventEmitter.emit(EVENTS.REACTIONS_SYNCED);

    } catch (error) {
      console.error('Background sync failed:', error);
    } finally {
      this.syncInProgress = false;
    }
  }

  // Clean up reactions that were created and deleted locally (never synced)
  private async cleanupLocalOnlyDeletedReactions(): Promise<void> {
    try {
      const localOnlyDeleted = await reactionTable
        .filter(r => r.deleted === true && r.local_only === true && r.last_synced === undefined)
        .toArray();

      if (localOnlyDeleted.length > 0) {
        console.log(`Cleaning up ${localOnlyDeleted.length} local-only deleted reactions`);
        
        for (const reaction of localOnlyDeleted) {
          await reactionTable.delete(reaction.id!);
        }
      }
    } catch (error) {
      console.error('Error cleaning up local-only deleted reactions:', error);
    }

  }

  // Sync a single reaction to the server
  private async syncSingleReaction(reaction: Reaction): Promise<void> {
    // Special case: If reaction was created and deleted locally without ever being synced,
    // just remove it from local database instead of trying to sync deletion
    if (reaction.deleted && reaction.local_only) {
      console.log(`Reaction ${reaction.id} was created and deleted offline - removing from local DB`);
      await reactionTable.delete(reaction.id!);
      return;
    }

    // Do not mark as synced until server confirms

    // STEP 2: Prepare endpoint and payload for background API call
    let endpoint = '';
    let payload = {};
    
    // Get reaction type ID if needed
    let reactionTypeId = reaction.reaction_type_id ?? reactionTypeIdFor(reaction.reaction_type_name, reaction.content_type);
    
    // Determine endpoint and payload based on reaction type and deletion status
    if (reaction.content_type === 'card') {
      if (reaction.deleted) {
        endpoint = `${API_BASE_URL}/api/removeHazardReaction`;
        payload = {
          hazardId: reaction.content_id,
          reactionType: reaction.reaction_type_name,
          id: reaction.id
        };
      } else {
        endpoint = `${API_BASE_URL}/api/addHazardReaction`;
        payload = {
          hazardId: reaction.content_id,
          reactionType: reaction.reaction_type_name,
          reactionTypeId: reactionTypeId,
          id: reaction.id
        };
      }
    } else { // comment
      if (reaction.deleted) {
        endpoint = `${API_BASE_URL}/api/removeCommentReaction`;
        payload = {
          commentId: reaction.content_id,
          reactionType: reaction.reaction_type_name,
          reactionId: reaction.id // Note: different field name for removal
        };
      } else {
        endpoint = `${API_BASE_URL}/api/addCommentReaction`;
        payload = {
          commentId: reaction.content_id,
          reactionType: reaction.reaction_type_name,
          id: reaction.id // Note: 'id' field for creation, not 'reactionTypeId'
        };
      }
    }
    
    // STEP 3: Make API call in background without waiting
    setTimeout(async () => {
      try {
        console.log(`Sending reaction ${reaction.id} via HTTP API...`);
        const response = await fetchWithAuth(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        
        // Handle different response scenarios
        if (response.ok) {
          console.log(`Successfully synced reaction ${reaction.id}`);
          
          // If this was a new reaction, try to get server ID from response
          if (!reaction.deleted) {
            try {
              const responseData = await response.json();
              if (responseData?.reactionId && responseData.reactionId !== reaction.id) {
                // Update with server-provided ID if different from local ID
                console.log(`Updating reaction ${reaction.id} with server ID ${responseData.reactionId}`);
                await reactionTable.update(reaction.id!, { id: responseData.reactionId });
              }
            } catch (e) {
              // If we can't get server ID, that's okay, we already marked it as synced
              console.warn(`Could not get server ID for reaction ${reaction.id}:`, e);
            }
            // Mark creation as synced
            await reactionTable.update(reaction.id!, {
              last_synced: moment().format(),
              local_only: false,
              deleted: false
            });
          } else {
            // Deletion: remove tombstone locally after success
            await reactionTable.delete(reaction.id!);
          }
        } else if (response.status === 404 && reaction.deleted) {
          // Deletion of non-existent reaction - this is OK, already marked as synced
          console.log(`Reaction ${reaction.id} doesn't exist on server (404) - deletion already marked as synced`);
          await reactionTable.delete(reaction.id!);
        } else if (response.status === 409 && !reaction.deleted) {
          // Conflict on creation (reaction already exists) - already marked as synced
          console.log(`Reaction ${reaction.id} already exists on server (409) - already marked as synced`);
          await reactionTable.update(reaction.id!, { last_synced: moment().format(), local_only: false });
        } else if (response.status === 400) {
          // Bad request - likely invalid data, log and keep marked as synced
          console.error(`Bad request for reaction ${reaction.id} (400) - keeping marked as synced`);
          const errorText = await response.text().catch(() => 'Unknown error');
          console.error(`Error details: ${errorText}`);
          // Keep pending for manual fix; do not mark as synced
        } else {
          // Other errors - revert sync status
          console.error(`Sync failed for reaction ${reaction.id} with status: ${response.status}, reverting sync status`);
          const errorText = await response.text().catch(() => 'Unknown error');
          console.error(`Error details: ${errorText}`);
          await reactionTable.update(reaction.id!, { last_synced: undefined, local_only: true });
        }
      } catch (error) {
        // Network errors or other issues - revert sync status
        console.error(`Network error syncing reaction ${reaction.id}:`, error);
        await reactionTable.update(reaction.id!, { last_synced: undefined, local_only: true });
      }
    }, 0);
  }

  // Debug method to inspect reactions for a specific user and hazard
  async debugReactionsForHazard(hazardId: string, userId?: string): Promise<void> {
    try {
      const currentUserId = userId || await this.getCurrentUserId();
      console.log(`🔍 DEBUG: Inspecting all reactions for hazard ${hazardId} and user ${currentUserId}`);
      
      const allReactions = await reactionTable.filter(r => 
        r.user_id === currentUserId && 
        r.content_type === 'card' && 
        r.content_id === hazardId
      ).toArray();

      console.log(`Found ${allReactions.length} total reactions:`, allReactions.map(r => ({
        id: r.id,
        reaction_type: r.reaction_type_name,
        deleted: r.deleted,
        local_only: r.local_only,
        last_synced: r.last_synced,
        created_at: r.created_at
      })));

      const activeReactions = allReactions.filter(r => !r.deleted);
      const deletedReactions = allReactions.filter(r => r.deleted);
      
      console.log(`Active reactions: ${activeReactions.length}`);
      console.log(`Deleted reactions: ${deletedReactions.length}`);
    } catch (error) {
      console.error('Error debugging reactions:', error);
    }
  }

  // Method to clear all reactions for a specific hazard and user (for debugging)
  async clearUserReactionsForHazard(hazardId: string, userId?: string): Promise<void> {
    try {
      const currentUserId = userId || await this.getCurrentUserId();
      console.log(`🧹 CLEARING: All reactions for hazard ${hazardId} and user ${currentUserId}`);
      
      const userReactions = await reactionTable.filter(r => 
        r.user_id === currentUserId && 
        r.content_type === 'card' && 
        r.content_id === hazardId
      ).toArray();

      console.log(`Found ${userReactions.length} reactions to clear`);
      
      for (const reaction of userReactions) {
        await reactionTable.delete(reaction.id!);
      }
      
      console.log(`✅ Cleared all reactions for hazard ${hazardId}`);
    } catch (error) {
      console.error('Error clearing reactions:', error);
    }
  }

  // Method to fix data inconsistencies by syncing with server
  async fixDataInconsistencies(hazardId?: string): Promise<void> {
    try {
      console.log(`🔧 FIX: Starting data inconsistency repair${hazardId ? ` for hazard ${hazardId}` : ' for all hazards'}`);
      
      if (!navigator.onLine) {
        console.warn('Cannot fix data inconsistencies while offline');
        return;
      }

      if (hazardId) {
        // Fix specific hazard
        console.log(`🔧 FIX: Fetching fresh server data for hazard ${hazardId}`);
        const response = await fetchWithAuth(`${API_BASE_URL}/api/getHazardReactions?hazardId=${hazardId}`);
        if (response.ok) {
          const view = readCardReactionBody(await response.json());
          await this.applyCardReactionSync(hazardId, view);
          
          // Trigger UI refresh
          eventEmitter.emit(EVENTS.REACTIONS_SYNCED);
        }
      } else {
        // Clear all local reactions and force a full resync
        console.log(`🔧 FIX: Clearing all local reaction data for full resync`);
        await reactionTable.clear();
        console.log(`✅ FIX: Cleared all local reactions - they will be reloaded from server as needed`);
        
        // Trigger UI refresh
        eventEmitter.emit(EVENTS.REACTIONS_SYNCED);
      }
      
    } catch (error) {
      console.error('Error fixing data inconsistencies:', error);
    }
  }

  // ============ Periodic Sync Management ============
  
  /**
   * Register displayed hazard IDs for periodic sync
   * This should be called when hazards are displayed on screen
   */
  public registerDisplayedHazards(hazardIds: string[]): void {
    hazardIds.forEach(id => this.displayedHazardIds.add(id));
    
    // Start periodic sync if not already running
    if (!this.periodicSyncInterval) {
      this.startPeriodicSync();
    }
    
    logger.debug(LogCategory.REACTIONS, `Registered ${hazardIds.length} displayed hazards. Total tracked: ${this.displayedHazardIds.size}`);
  }

  /**
   * Replace the tracked displayed-hazard set (viewport-visible rows only).
   * Limits periodic batch reaction sync to visible cards instead of every loaded row.
   */
  public replaceDisplayedHazards(hazardIds: string[]): void {
    this.displayedHazardIds.clear();
    hazardIds.forEach(id => this.displayedHazardIds.add(id));

    if (this.displayedHazardIds.size === 0) {
      this.stopPeriodicSync();
    } else if (!this.periodicSyncInterval) {
      this.startPeriodicSync();
    }

    logger.debug(
      LogCategory.REACTIONS,
      `Displayed hazard set replaced: ${hazardIds.length} visible, total tracked: ${this.displayedHazardIds.size}`
    );
  }
  
  /**
   * Unregister displayed hazard IDs
   * This should be called when hazards are no longer displayed
   */
  public unregisterDisplayedHazards(hazardIds: string[]): void {
    hazardIds.forEach(id => this.displayedHazardIds.delete(id));
    
    // Stop periodic sync if no hazards are being tracked
    if (this.displayedHazardIds.size === 0 && this.periodicSyncInterval) {
      this.stopPeriodicSync();
    }
    
    logger.debug(LogCategory.REACTIONS, `Unregistered ${hazardIds.length} displayed hazards. Total tracked: ${this.displayedHazardIds.size}`);
  }
  
  /**
   * Manually trigger periodic sync for testing/debugging purposes
   */
  public async triggerPeriodicSync(): Promise<void> {
    logger.debug(LogCategory.REACTIONS, 'Manual periodic sync triggered');
    await this.performPeriodicSync();
  }
  
  /**
   * Get current sync status for debugging
   */
  public getSyncStatus(): { 
    isRunning: boolean; 
    trackedHazards: number; 
    lastSync: number; 
    intervalMs: number 
  } {
    return {
      isRunning: this.periodicSyncInterval !== null,
      trackedHazards: this.displayedHazardIds.size,
      lastSync: this.lastPeriodicSync,
      intervalMs: this.PERIODIC_SYNC_INTERVAL_MS
    };
  }
  
  /**
   * Start the periodic sync interval
   */
  private startPeriodicSync(): void {
    if (this.periodicSyncInterval) {
      return; // Already running
    }
    
    logger.debug(LogCategory.REACTIONS, 'Starting periodic reaction sync');
    
    this.periodicSyncInterval = setInterval(() => {
      this.performPeriodicSync();
    }, this.PERIODIC_SYNC_INTERVAL_MS);
  }
  
  /**
   * Stop the periodic sync interval
   */
  private stopPeriodicSync(): void {
    if (this.periodicSyncInterval) {
      clearInterval(this.periodicSyncInterval);
      this.periodicSyncInterval = null;
      logger.debug(LogCategory.REACTIONS, 'Stopped periodic reaction sync');
    }
  }
  
  /**
   * Perform periodic sync for all displayed hazards
   * This aggregates reactions from the server and updates local state
   */
  private async performPeriodicSync(): Promise<void> {
    const now = Date.now();
    
    // Respect minimum sync gap to avoid overwhelming the server
    if (now - this.lastPeriodicSync < this.MIN_SYNC_GAP_MS) {
      logger.debug(LogCategory.REACTIONS, 'Skipping periodic sync - too soon since last sync');
      return;
    }
    
    // Skip if offline or no hazards to sync
    if (!navigator.onLine || this.displayedHazardIds.size === 0) {
      return;
    }
    
    try {
      this.lastPeriodicSync = now;
      const hazardIds = Array.from(this.displayedHazardIds);
      
      logger.debug(LogCategory.REACTIONS, `Starting periodic sync for ${hazardIds.length} displayed hazards`);
      
      // Use batch fetch to get all reactions for displayed hazards
      const batchResults = await this.getBatchHazardReactions(hazardIds);
      
      // Perform aggregation and update local state
      await this.aggregateAndUpdateReactions(batchResults);
      
      logger.debug(LogCategory.REACTIONS, `Completed periodic sync for ${hazardIds.length} hazards`);
      
    } catch (error) {
      logger.reactionsWarn('Error during periodic reaction sync', error);
    }
  }
  
  /**
   * Aggregate reactions and update local state
   * This ensures reaction counts are accurate by comparing with server data
   */
  private async aggregateAndUpdateReactions(batchResults: Record<string, Reaction[]>): Promise<void> {
    try {
      const currentUserId = await this.getCurrentUserId();
      if (!currentUserId) {
        logger.reactionsWarn('Cannot aggregate reactions - user not authenticated');
        return;
      }
      
      let totalUpdated = 0;
      
      for (const [hazardId, serverReactions] of Object.entries(batchResults)) {
        // Get current local reactions for this hazard
        const localReactions = await reactionTable.filter(r => 
          r.content_type === 'card' && 
          r.content_id === hazardId && 
          !r.deleted
        ).toArray();
        
        // Create a map of server reactions by user_id + reaction_type_name
        const serverReactionMap = new Map<string, Reaction>();
        serverReactions.forEach(reaction => {
          const key = `${reaction.user_id}_${reaction.reaction_type_name}`;
          serverReactionMap.set(key, reaction);
        });
        
        // Create a map of local reactions by user_id + reaction_type_name
        const localReactionMap = new Map<string, Reaction>();
        localReactions.forEach(reaction => {
          const key = `${reaction.user_id}_${reaction.reaction_type_name}`;
          localReactionMap.set(key, reaction);
        });
        
        let hazardUpdated = false;
        
        // Add missing reactions from server
        const serverReactionEntries = Array.from(serverReactionMap.entries());
        for (let i = 0; i < serverReactionEntries.length; i++) {
          const [key, serverReaction] = serverReactionEntries[i];
          if (!localReactionMap.has(key)) {
            // This reaction exists on server but not locally - add it
            const reactionToAdd: Reaction = {
              id: serverReaction.id,
              user_id: serverReaction.user_id,
              reaction_type_name: serverReaction.reaction_type_name,
              content_type: 'card',
              content_id: hazardId,
              created_at: serverReaction.created_at,
              deleted: false,
              local_only: false,
              last_synced: new Date().toISOString()
            };
            
            try {
              await reactionTable.add(reactionToAdd);
              hazardUpdated = true;
              logger.debug(LogCategory.REACTIONS, `Added missing reaction ${key} for hazard ${hazardId}`);
            } catch (error: any) {
              if (error.name === 'ConstraintError') {
                // Reaction already exists, update it instead
                await reactionTable.update(serverReaction.id, {
                  deleted: false,
                  local_only: false,
                  last_synced: new Date().toISOString()
                });
                hazardUpdated = true;
              } else {
                throw error;
              }
            }
          }
        }
        
        // Remove reactions that exist locally but not on server (unless they're local-only)
        const localReactionEntries = Array.from(localReactionMap.entries());
        for (let i = 0; i < localReactionEntries.length; i++) {
          const [key, localReaction] = localReactionEntries[i];
          if (!serverReactionMap.has(key) && !localReaction.local_only) {
            // This reaction exists locally but not on server - mark as deleted
            await reactionTable.update(localReaction.id!, {
              deleted: true,
              last_synced: new Date().toISOString()
            });
            hazardUpdated = true;
            logger.debug(LogCategory.REACTIONS, `Marked missing reaction ${key} as deleted for hazard ${hazardId}`);
          }
        }
        
        if (hazardUpdated) {
          totalUpdated++;
          // Emit event to update UI
          eventEmitter.emit(EVENTS.REACTIONS_SYNCED, hazardId);
        }
      }
      
      if (totalUpdated > 0) {
        logger.debug(LogCategory.REACTIONS, `Periodic sync updated reactions for ${totalUpdated} hazards`);
      }
      
    } catch (error) {
      logger.reactionsWarn('Error aggregating reactions', error);
    }
  }

  // Cleanup method to remove event listeners (for proper cleanup if needed)
  public destroy(): void {
    eventEmitter.off(EVENTS.REACTION_ADDED, this.handleIncomingReactionAdded.bind(this));
    eventEmitter.off(EVENTS.REACTION_DELETED, this.handleIncomingReactionRemoved.bind(this));
    
    // Clear request caches
    this.ongoingHazardRequests.clear();
    this.ongoingCommentRequests.clear();
    
    // Stop periodic sync
    this.stopPeriodicSync();
    this.displayedHazardIds.clear();
  }
}

const reactionService = new ReactionService();

export default reactionService;